import {resolveRelative} from '@jetbrains/teamcity-ui/dist/utils'
import {trackEvent} from '@jetbrains/teamcity-ui/statistics'

import {PipelineRunTabs} from '../../components/pages/PipelinesPages/PipelineRunPage/PipelineRunPage.consts'
import Routes, {
  isAgentsScreen,
  isPipelinesEditScreen,
  isProjectSidebarScreen,
  isQueueScreen,
  matchRoute,
} from '../../routes'
import {
  BuildPageTabNamesEnum,
  BuildTypePageTabNamesEnum,
  ProjectPageTabNamesEnum,
} from '../../types'
import {BS} from '../../types/BS_types'
import {queryToObject} from '../../utils/queryParams'

enum PageArea {
  USER = 'user',
  GLOBAL_ADMIN = 'global_admin',
  PROJECT_ADMIN = 'project_admin',
  AGENTS = 'agents',
  QUEUE = 'queue',
  LEARN = 'learn',
  CHANGES = 'changes',
  OTHER = 'other',
}
enum ProjectTab {
  OVERVIEW = 'overview',
  CHANGE_LOG = 'change_log',
  PROBLEMS = 'problems',
  STATISTICS = 'statistics',
  FLACKY_TESTS = 'flacky_tests',
  CUSTOM = 'custom',
}
enum ConfigurationPageTab {
  BUILD_TYPE_HISTORY_LIST = 'buildTypeHistoryList', // History
  BUILD_TYPE_STATUS_DIV = 'buildTypeStatusDiv', // Overview
  BUILD_TYPE_CHANGE_LOG = 'buildTypeChangeLog',
  PENDING_CHANGES_DIV = 'pendingChangesDiv',
  BUILD_TYPE_CHAINS = 'buildTypeChains',
  BUILD_TYPE_ISSUE_LOG = 'buildTypeIssueLog',
  BUILD_TYPE_STATISTICS = 'buildTypeStatistics',
  COMPATIBILITY_LIST = 'compatibilityList',
  BUILD_TYPE_SETTINGS = 'buildTypeSettings',
  MAVEN = 'maven',
  BUILD_TYPE_BRANCHES = 'buildTypeBranches',
  AGENT_BROWSE = 'agentBrowse',
  CUSTOM = 'custom',
}
enum BuildPageTab {
  BUILD_RESULTS_DIV = 'buildResultsDiv', // Overview
  QUEUED_BUILD_OVERVIEW_TAB = 'queuedBuildOverviewTab',
  BUILD_USED_RESOURCES = 'buildUsedResources',
  BUILD_CHANGES_DIV = 'buildChangesDiv',
  TESTS_INFO = 'testsInfo',
  BUILD_LOG = 'buildLog',
  BUILD_PARAMETERS = 'buildParameters',
  QUEUED_BUILD_COMPATIBILITY_TAB = 'queuedBuildCompatibilityTab',
  DEPENDENCIES = 'dependencies',
  ARTIFACTS = 'artifacts',
  BUILD_ISSUES = 'buildIssues',
  PERFORMANCE_MONITOR = 'performance_monitor',
  DOCKER_BUILD_INFO = 'dockerBuildInfo',
  MAVEN_BUILD_INFO = 'mavenBuildInfo',
  DUPLICATOR = 'Duplicator',
  INSPECTION = 'Inspection',
  COVERAGE_GO = 'coverage_go',
  COVERAGE_DOTNET = 'coverage_dotnet',
  COVERAGE_IDEA = 'coverage_idea',
  COVERAGE_EMMA = 'coverage_emma',
  COVERAGE_JACOCO = 'coverage_jacoco',
  FXCOP_REPORT_TAB = 'fxcopReportTab',
  CUSTOM = 'custom',
}

type PageOpenPayload = {
  buildId?: string | null
  configurationId?: string | null
  projectId?: string | null
  area: PageArea
  project_tab?: ProjectTab
  configuration_tab?: ConfigurationPageTab
  build_tab?: BuildPageTab
  pipeline_tab?: BuildPageTab
  job_tab?: BuildPageTab
}

const projectTabs: Record<string, ProjectTab> = {
  // Sakura UI tab ids
  [ProjectPageTabNamesEnum.OVERVIEW]: ProjectTab.OVERVIEW,
  [ProjectPageTabNamesEnum.CHANGE_LOG]: ProjectTab.CHANGE_LOG,
  [ProjectPageTabNamesEnum.PROBLEMS]: ProjectTab.PROBLEMS,
  [ProjectPageTabNamesEnum.STATISTICS]: ProjectTab.STATISTICS,
  [ProjectPageTabNamesEnum.flakyTests]: ProjectTab.FLACKY_TESTS,
  // Classic UI tab ids (via ClassicUIProjectTabNamesEnum)
  [ProjectPageTabNamesEnum.projectOverview]: ProjectTab.OVERVIEW,
  [ProjectPageTabNamesEnum.projectChangeLog]: ProjectTab.CHANGE_LOG,
  [ProjectPageTabNamesEnum.problems]: ProjectTab.PROBLEMS,
  [ProjectPageTabNamesEnum.stats]: ProjectTab.STATISTICS,
}

const buildTypeTabs: Record<string, ConfigurationPageTab> = {
  [BuildTypePageTabNamesEnum.OVERVIEW]: ConfigurationPageTab.BUILD_TYPE_STATUS_DIV,
  [BuildTypePageTabNamesEnum.CHANGE_LOG]: ConfigurationPageTab.BUILD_TYPE_CHANGE_LOG,
  [BuildTypePageTabNamesEnum.PENDING_CHANGES]: ConfigurationPageTab.PENDING_CHANGES_DIV,
  [BuildTypePageTabNamesEnum.CHAINS]: ConfigurationPageTab.BUILD_TYPE_CHAINS,
  [BuildTypePageTabNamesEnum.buildTypeIssueLog]: ConfigurationPageTab.BUILD_TYPE_ISSUE_LOG,
  [BuildTypePageTabNamesEnum.STATISTICS]: ConfigurationPageTab.BUILD_TYPE_STATISTICS,
  [BuildTypePageTabNamesEnum.compatibilityList]: ConfigurationPageTab.COMPATIBILITY_LIST,
  [BuildTypePageTabNamesEnum.buildTypeSettings]: ConfigurationPageTab.BUILD_TYPE_SETTINGS,
  [BuildTypePageTabNamesEnum.maven]: ConfigurationPageTab.MAVEN,
  [BuildTypePageTabNamesEnum.agentBrowse]: ConfigurationPageTab.AGENT_BROWSE,
}

const buildTabs: Record<string, BuildPageTab> = {
  [BuildPageTabNamesEnum.OVERVIEW]: BuildPageTab.BUILD_RESULTS_DIV,
  [BuildPageTabNamesEnum.buildUsedResources]: BuildPageTab.BUILD_USED_RESOURCES,
  [BuildPageTabNamesEnum.CHANGES]: BuildPageTab.BUILD_CHANGES_DIV,
  [BuildPageTabNamesEnum.TESTS]: BuildPageTab.TESTS_INFO,
  [BuildPageTabNamesEnum.LOG]: BuildPageTab.BUILD_LOG,
  [BuildPageTabNamesEnum.PARAMETERS]: BuildPageTab.BUILD_PARAMETERS,
  [BuildPageTabNamesEnum.queuedBuildCompatibilityTab]: BuildPageTab.QUEUED_BUILD_COMPATIBILITY_TAB,
  [BuildPageTabNamesEnum.DEPENDENCIES]: BuildPageTab.DEPENDENCIES,
  [BuildPageTabNamesEnum.ARTIFACTS]: BuildPageTab.ARTIFACTS,
  [BuildPageTabNamesEnum.buildIssues]: BuildPageTab.BUILD_ISSUES,
  [BuildPageTabNamesEnum.perfmon]: BuildPageTab.PERFORMANCE_MONITOR,
  [BuildPageTabNamesEnum.dockerBuildInfo]: BuildPageTab.DOCKER_BUILD_INFO,
  [BuildPageTabNamesEnum.mavenBuildInfo]: BuildPageTab.MAVEN_BUILD_INFO,
  [BuildPageTabNamesEnum.Duplicator]: BuildPageTab.DUPLICATOR,
  [BuildPageTabNamesEnum.Inspection]: BuildPageTab.INSPECTION,
  [BuildPageTabNamesEnum.coverage_go]: BuildPageTab.COVERAGE_GO,
  [BuildPageTabNamesEnum.coverage_dotnet]: BuildPageTab.COVERAGE_DOTNET,
  [BuildPageTabNamesEnum.coverage_idea]: BuildPageTab.COVERAGE_IDEA,
  [BuildPageTabNamesEnum.coverage_emma]: BuildPageTab.COVERAGE_EMMA,
  [BuildPageTabNamesEnum.coverage_jacoco]: BuildPageTab.COVERAGE_JACOCO,
  [BuildPageTabNamesEnum.fxcopReportTab]: BuildPageTab.FXCOP_REPORT_TAB,
}

function getPageOpenPayload(): PageOpenPayload {
  const queryParams = queryToObject(location.search)

  const projectMatch = matchRoute(Routes.PROJECT, location.pathname)
  if (projectMatch) {
    const {projectId} = projectMatch.params
    const {projectTab} = queryParams
    return {
      area: PageArea.USER,
      projectId,
      project_tab: projectTabs[projectTab || ProjectPageTabNamesEnum.OVERVIEW] ?? ProjectTab.CUSTOM,
    }
  }

  const buildTypeMatch = matchRoute(Routes.BUILD_TYPE, location.pathname)
  if (buildTypeMatch) {
    const {buildTypeId} = buildTypeMatch.params
    const {buildTypeTab} = queryParams
    return {
      area: PageArea.USER,
      configurationId: buildTypeId,
      configuration_tab:
        buildTypeTabs[buildTypeTab || BuildTypePageTabNamesEnum.OVERVIEW] ??
        ConfigurationPageTab.CUSTOM,
    }
  }

  const buildMatch = matchRoute(Routes.BUILD, location.pathname)
  if (buildMatch) {
    const {buildId, buildTypeId} = buildMatch.params
    const {buildTab} = queryParams
    return {
      area: PageArea.USER,
      buildId,
      configurationId: buildTypeId,
      build_tab: buildTabs[buildTab || BuildPageTabNamesEnum.OVERVIEW] ?? BuildPageTab.CUSTOM,
    }
  }

  const compareBuildsMatch = matchRoute(Routes.COMPARE_BUILDS, location.pathname)
  if (compareBuildsMatch) {
    const {buildIdSource} = compareBuildsMatch.params
    return {area: PageArea.USER, buildId: buildIdSource}
  }

  const pipelineMatch =
    matchRoute(Routes.PIPELINE_RUN, location.pathname) ??
    matchRoute(Routes.PIPELINE, location.pathname)
  if (pipelineMatch) {
    const {pipelineRunId, pipelineId} = pipelineMatch.params
    const {job, tab, pipelineRunTab} = queryParams
    const commonPayload = {
      area: isPipelinesEditScreen(queryParams) ? PageArea.PROJECT_ADMIN : PageArea.USER,
      buildId: pipelineRunId,
      projectId: pipelineId,
    }
    switch (pipelineRunTab) {
      case PipelineRunTabs.CHAIN:
        return {...commonPayload, pipeline_tab: BuildPageTab.DEPENDENCIES}
      case PipelineRunTabs.ARTIFACTS:
        return {...commonPayload, pipeline_tab: BuildPageTab.ARTIFACTS}
      default:
        if (job != null) {
          return {
            ...commonPayload,
            job_tab: buildTabs[tab || BuildPageTabNamesEnum.OVERVIEW] ?? BuildPageTab.CUSTOM,
          }
        } else {
          return {
            ...commonPayload,
            pipeline_tab: buildTabs[tab || BuildPageTabNamesEnum.OVERVIEW] ?? BuildPageTab.CUSTOM,
          }
        }
    }
  }

  if (isProjectSidebarScreen(location.pathname)) {
    return {area: PageArea.USER}
  }

  if (BS?.AdminNavigation?.projectAdminPages?.some(page => location.pathname.endsWith(page))) {
    const {projectId, id} = queryParams
    const [_, configurationId] = id?.match(/^buildType:(.*)$/) ?? []
    return {area: PageArea.PROJECT_ADMIN, projectId, configurationId}
  }
  if (location.href.startsWith(resolveRelative('/admin'))) {
    return {area: PageArea.GLOBAL_ADMIN}
  }

  if (isAgentsScreen(location.pathname)) {
    return {area: PageArea.AGENTS}
  }

  if (isQueueScreen(location.pathname)) {
    return {area: PageArea.QUEUE}
  }

  if (matchRoute(Routes.GUIDES, location.pathname) != null) {
    return {area: PageArea.LEARN}
  }

  if (
    matchRoute(Routes.CHANGES, location.pathname) != null ||
    matchRoute(Routes.CHANGE, location.pathname) != null
  ) {
    return {area: PageArea.CHANGES}
  }

  return {area: PageArea.OTHER}
}

const trackPageOpen = () =>
  trackEvent({name: 'page.open', groupId: 'page.events.group', data: getPageOpenPayload()})

export function trackPageNavigation() {
  trackPageOpen()
  let prevLocation = {...location}
  function isPageChange() {
    const prevQueryParams = queryToObject(prevLocation.search)
    const queryParams = queryToObject(location.search)
    return (
      location.pathname !== prevLocation.pathname ||
      isPipelinesEditScreen(queryParams) !== isPipelinesEditScreen(prevQueryParams) ||
      queryParams.tab !== prevQueryParams.tab ||
      queryParams.buildTab !== prevQueryParams.buildTab ||
      queryParams.buildTypeTab !== prevQueryParams.buildTypeTab ||
      queryParams.projectTab !== prevQueryParams.projectTab ||
      queryParams.testTab !== prevQueryParams.testTab ||
      queryParams.pipelineRunTab !== prevQueryParams.pipelineRunTab ||
      queryParams.job !== prevQueryParams.job
    )
  }
  function onNavigate() {
    if (isPageChange()) {
      trackPageOpen()
    }
    prevLocation = {...location}
  }

  const originalPushState = history.pushState
  history.pushState = function pushState(...args) {
    originalPushState.apply(this, args)
    onNavigate()
  }
  addEventListener('popstate', onNavigate)
}
